import { useCallback } from 'react';
import { toast } from 'react-toastify';
import {
    useConnection,
    useWriteContract,
    usePublicClient,
} from 'wagmi';
import { parseUnits } from 'viem';
import {
    DEFAULT_ERROR_HEADING,
    GAS_FEES_ERROR,
} from '../../utils/ConstantMessages';
import { notifyError } from '../../components/Toast';
import BalanceOfAbi from '../../utils/BalanceOfAbi.json';

export const useTransfer = () => {
    const { address: account, isConnected } = useConnection();
    const publicClient = usePublicClient();
    const transfer = useWriteContract();

    const transferFunds = useCallback(
        async (toAddress, amount, tokenConfig) => {
            try {
                if (!isConnected || !account) {
                    toast.error('Wallet not connected');
                    return false;
                }

                const tokenAmount = parseUnits(
                    amount.toString(),
                    tokenConfig.tokenDecimals
                );

                const gasPrice = await publicClient.getGasPrice();
                const adjustedGasPrice = gasPrice * 2n;

                const gasEstimate = await publicClient.estimateContractGas({
                    address: tokenConfig.tokenAddress,
                    abi: BalanceOfAbi,
                    functionName: 'transfer',
                    args: [toAddress, tokenAmount],
                    account,
                });

                const totalGasCost = gasEstimate * adjustedGasPrice;
                const nativeBalance = await publicClient.getBalance({
                    address: account,
                });

                if (nativeBalance < totalGasCost) {
                    notifyError(DEFAULT_ERROR_HEADING, GAS_FEES_ERROR);
                    return false;
                }

                const hash = await transfer.mutateAsync({
                    address: tokenConfig.tokenAddress,
                    abi: BalanceOfAbi,
                    functionName: 'transfer',
                    args: [toAddress, tokenAmount],
                    gas: gasEstimate,
                    gasPrice: adjustedGasPrice,
                });

                return { transactionHash: hash, hash };
            } catch (error) {
                console.error('Transfer error:', error);
                toast.error(
                    error?.shortMessage ||
                    error?.message ||
                    'Transaction failed'
                );
                throw error;
            }
        },
        [account, isConnected, publicClient, transfer]
    );

    return transferFunds;
};
